/**
 * Mobile performance optimizations for the team browser
 */

import React, { useMemo, useCallback, useRef, useEffect } from 'react'

// Debounce hook for search input
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Throttle hook for scroll events
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const lastRun = useRef(Date.now())

  return useCallback(
    ((...args) => {
      if (Date.now() - lastRun.current >= delay) {
        callback(...args)
        lastRun.current = Date.now()
      }
    }) as T,
    [callback, delay]
  )
}

// Virtual scrolling for large team lists
export function useVirtualScrolling(
  items: any[],
  itemHeight: number,
  containerHeight: number
) {
  const [scrollTop, setScrollTop] = React.useState(0)

  const visibleItems = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight)
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      items.length
    )

    return {
      startIndex,
      endIndex,
      items: items.slice(startIndex, endIndex),
      totalHeight: items.length * itemHeight,
      offsetY: startIndex * itemHeight
    }
  }, [items, itemHeight, containerHeight, scrollTop])

  const handleScroll = useThrottle((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop)
  }, 16) // ~60fps

  return {
    visibleItems,
    handleScroll,
    totalHeight: visibleItems.totalHeight,
    offsetY: visibleItems.offsetY
  }
}

// Optimized search filtering
export function useOptimizedSearch(
  items: any[],
  searchTerm: string,
  searchFields: string[]
) {
  return useMemo(() => {
    if (!searchTerm.trim()) return items

    const query = searchTerm.toLowerCase()
    
    return items.filter(item => {
      return searchFields.some(field => {
        const value = item[field]
        return value && value.toLowerCase().includes(query)
      })
    })
  }, [items, searchTerm, searchFields])
}

// Memoized team card props to prevent unnecessary re-renders
export function useTeamCardProps(team: any, isSpotted: boolean, callbacks: any) {
  return useMemo(() => ({
    team,
    isSpotted,
    spotButtonState: {
      text: isSpotted ? "Spotted" : "Spot",
      variant: isSpotted ? "secondary" : "default",
      disabled: isSpotted,
      loading: false,
      isSpotted
    },
    ...callbacks
  }), [team, isSpotted, callbacks])
}

// Image lazy loading hook
export function useLazyImage(src: string, placeholder: string) {
  const [imageSrc, setImageSrc] = React.useState(placeholder)
  const [isLoaded, setIsLoaded] = React.useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const img = new Image()
          img.onload = () => {
            setImageSrc(src)
            setIsLoaded(true)
          }
          img.src = src
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => observer.disconnect()
  }, [src])

  return { imageSrc, isLoaded, imgRef }
}

// Touch gesture optimization
export function useTouchOptimization() {
  useEffect(() => {
    // Prevent zoom on double tap for better UX
    let lastTouchEnd = 0
    
    const preventZoom = (e: TouchEvent) => {
      const now = Date.now()
      if (now - lastTouchEnd <= 300) {
        e.preventDefault()
      }
      lastTouchEnd = now
    }

    document.addEventListener('touchend', preventZoom, { passive: false })
    
    return () => {
      document.removeEventListener('touchend', preventZoom)
    }
  }, [])
}

// Memory cleanup for mobile
export function useMemoryCleanup() {
  useEffect(() => {
    const cleanup = () => {
      // Force garbage collection if available (development only)
      if (process.env.NODE_ENV === 'development' && 'gc' in window) {
        (window as any).gc()
      }
    }

    // Cleanup on page visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cleanup()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      cleanup()
    }
  }, [])
}

// Optimized filter state management
export function useOptimizedFilters() {
  const [filters, setFilters] = React.useState(new Map<string, any>())

  const addFilter = useCallback((key: string, value: any) => {
    setFilters(prev => new Map(prev).set(key, value))
  }, [])

  const removeFilter = useCallback((key: string) => {
    setFilters(prev => {
      const newFilters = new Map(prev)
      newFilters.delete(key)
      return newFilters
    })
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(new Map())
  }, [])

  const hasFilters = filters.size > 0

  return {
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    hasFilters,
    filterArray: Array.from(filters.entries())
  }
}

// Performance monitoring for mobile
export function usePerformanceMonitoring() {
  const metricsRef = useRef({
    renderCount: 0,
    lastRenderTime: Date.now(),
    averageRenderTime: 0
  })

  useEffect(() => {
    const metrics = metricsRef.current
    const now = Date.now()
    const renderTime = now - metrics.lastRenderTime

    metrics.renderCount++
    metrics.averageRenderTime = (metrics.averageRenderTime + renderTime) / 2
    metrics.lastRenderTime = now

    // Log performance warnings in development (debounced to reduce console noise)
    if (process.env.NODE_ENV === 'development') {
      // Only warn about significantly slow renders (>200ms) and batch them
      if (renderTime > 200 && metrics.renderCount % 10 === 0) {
        console.warn(`Slow render detected: ${renderTime}ms (avg: ${metrics.averageRenderTime.toFixed(0)}ms)`)
      }

      // Reduce frequency of average render time logs
      if (metrics.renderCount % 100 === 0) {
        console.log(`Average render time: ${metrics.averageRenderTime.toFixed(2)}ms over ${metrics.renderCount} renders`)
      }
    }
  })

  return metricsRef.current
}

// Batch updates for better performance
export function useBatchedUpdates() {
  const [updates, setUpdates] = React.useState<(() => void)[]>([])
  const timeoutRef = useRef<NodeJS.Timeout>()

  const batchUpdate = useCallback((updateFn: () => void) => {
    setUpdates(prev => [...prev, updateFn])
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    
    timeoutRef.current = setTimeout(() => {
      setUpdates(currentUpdates => {
        currentUpdates.forEach(fn => fn())
        return []
      })
    }, 16) // Next frame
  }, [])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return batchUpdate
}

// Optimized component re-render prevention
export const MemoizedTeamCard = React.memo(
  ({ team, isSpotted, onSpot, onViewDetails, spotButtonState }: any) => {
    // Component implementation would go here
    return null // Placeholder
  },
  (prevProps, nextProps) => {
    // Custom comparison function
    return (
      prevProps.team.id === nextProps.team.id &&
      prevProps.isSpotted === nextProps.isSpotted &&
      prevProps.spotButtonState.loading === nextProps.spotButtonState.loading
    )
  }
)